import { inject, Injectable, signal } from '@angular/core';
import {
  buildPlanningAttachmentSeed,
  PlanningAttachment,
  PlanningAttachmentCategory,
  PlanningCorrespondence,
} from '#data/planning-attachments';
import { RLWorkflowApiService } from './rl-workflow-api.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

interface PlanningCaseAttachmentState {
  attachments: PlanningAttachment[];
  correspondence: PlanningCorrespondence[];
}

export interface NewPlanningAttachment {
  name: string;
  fileName: string;
  category: PlanningAttachmentCategory;
  sizeLabel: string;
}

@Injectable({ providedIn: 'root' })
export class PlanningAttachmentsStoreService {
  private _rlApi = inject(RLWorkflowApiService);
  private _caseStates = signal<Record<string, PlanningCaseAttachmentState>>({});

  caseStates = this._caseStates.asReadonly();

  ensureCaseState(caseId: string, wellName: string): void {
    this._ensureCaseState(caseId, wellName);
  }

  getCaseState(caseId: string, wellName: string): PlanningCaseAttachmentState {
    return this._cloneState(this._ensureCaseState(caseId, wellName));
  }

  /**
   * Fetches attachments from the API and writes them into the shared caseStates signal
   * so any component reading from caseStates() automatically reflects the loaded list.
   */
  loadAndStoreAttachments(
    caseId: string,
    rlCaseId: number,
    wellName = '',
  ): Observable<PlanningAttachment[]> {
    const currentState = this._ensureCaseState(caseId, wellName);

    return this.fetchAttachments(rlCaseId).pipe(
      tap((attachments) => {
        this._caseStates.update((states) => ({
          ...states,
          [caseId]: {
            ...(states[caseId] ?? currentState),
            attachments,
          },
        }));
      }),
    );
  }

  fetchCorrespondenceAttachments(
    rlCaseId: number,
  ): Observable<PlanningCorrespondence[]> {
    return forkJoin({
      attachments: this._rlApi
        .getAttachments(rlCaseId)
        .pipe(catchError(() => of([]))),
      notifLog: this._rlApi
        .getNotificationLog(rlCaseId)
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ attachments, notifLog }) => {
        // Build a lookup from blob path → semicolon-separated recipients
        const recipientsByBlobPath = new Map<string, string>();
        for (const entry of notifLog) {
          if (entry.emlBlobPath) {
            recipientsByBlobPath.set(entry.emlBlobPath, entry.recipients);
          }
        }

        return attachments
          .filter((a) => a.attachmentType === 'Correspondence')
          .map((a) => {
            // Try matching by the blob path suffix (caseNumber/fileName)
            let recipient = '';
            for (const [path, recipients] of recipientsByBlobPath) {
              if (path.endsWith(`/${a.fileName}`)) {
                recipient = recipients;
                break;
              }
            }
            return {
              id: `correspondence-${a.id}`,
              fileName: a.fileName,
              notification: 'Correspondence',
              recipient,
              sentOn: new Date(a.uploadedDate).toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
              }),
              status: 'Sent',
              hasDownload: true,
            };
          });
      }),
      catchError(() => of([])),
    );
  }

  fetchAttachments(rlCaseId: number): Observable<PlanningAttachment[]> {
    return this._rlApi.getAttachments(rlCaseId).pipe(
      map((apiAttachments) =>
        apiAttachments
          .filter(
            (attachment) => attachment.attachmentType !== 'Correspondence',
          )
          .map((attachment) => ({
            id: `attachment-${attachment.id}`,
            name: attachment.fileName.replace(/\.[^/.]+$/, ''), // Remove extension
            fileName: attachment.fileName,
            category: this._mapAttachmentTypeToCategory(
              attachment.attachmentType,
            ),
            attachmentType: attachment.attachmentType,
            uploadedBy: attachment.uploadedBy?.trim() || '—',
            uploadedOn: new Date(attachment.uploadedDate).toLocaleString(
              'en-US',
              {
                hour: '2-digit',
                minute: '2-digit',
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
              },
            ),
            sizeLabel:
              attachment.fileSizeBytes > 0
                ? `${Math.max(1, Math.round(attachment.fileSizeBytes / 1024))} KB`
                : 'N/A', // API doesn't provide file size for legacy rows
          })),
      ),
      catchError(() => of([])),
    );
  }

  private _mapAttachmentTypeToCategory(
    attachmentType: string,
  ): PlanningAttachmentCategory {
    if (attachmentType === 'JobPlanPacket') {
      return 'Job Plan Packet';
    }
    if (attachmentType === 'FinalReport') {
      return 'Final Report';
    }
    return 'File';
  }

  removeAttachment(caseId: string, attachmentId: string): void {
    this._caseStates.update((states) => {
      const current = states[caseId];
      if (!current) return states;
      return {
        ...states,
        [caseId]: {
          ...current,
          attachments: current.attachments.filter((a) => a.id !== attachmentId),
        },
      };
    });
  }

  addAttachments(
    caseId: string,
    wellName: string,
    attachments: NewPlanningAttachment[],
  ): PlanningCaseAttachmentState {
    if (attachments.length === 0) {
      return this.getCaseState(caseId, wellName);
    }

    const currentState = this._ensureCaseState(caseId, wellName);
    const uploadedOn = this._formatTimestamp(new Date());
    const createdAttachments = attachments.map((attachment, index) => ({
      id: `${caseId}-attachment-user-${Date.now()}-${index}`,
      name: attachment.name,
      fileName: attachment.fileName,
      category: attachment.category,
      uploadedBy: 'Current User',
      uploadedOn,
      sizeLabel: attachment.sizeLabel,
    }));

    this._caseStates.update((states) => ({
      ...states,
      [caseId]: {
        ...currentState,
        attachments: [...createdAttachments, ...currentState.attachments],
      },
    }));

    return this.getCaseState(caseId, wellName);
  }

  private _ensureCaseState(
    caseId: string,
    wellName: string,
  ): PlanningCaseAttachmentState {
    const existingState = this._caseStates()[caseId];

    if (existingState) {
      return existingState;
    }

    const seededState = buildPlanningAttachmentSeed(caseId, wellName);

    this._caseStates.update((states) => ({
      ...states,
      [caseId]: seededState,
    }));

    return seededState;
  }

  private _cloneState(
    state: PlanningCaseAttachmentState,
  ): PlanningCaseAttachmentState {
    return {
      attachments: state.attachments.map((attachment) => ({ ...attachment })),
      correspondence: state.correspondence.map((item) => ({ ...item })),
    };
  }

  private _formatTimestamp(date: Date): string {
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }
}
